/**
 * The state and refs every part of `useAgentChat` shares.
 *
 * Split out of `useAgentChat.ts` with no behavior change. The refs and setters
 * travel as one `ChatCore` object, built once per mount, so the sibling hooks
 * that read or write them take one stable argument instead of a dozen.
 */
import { useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { loadStoredSession } from './agentChatSessionStorage';
import type { ChatMessage } from './agentChatReducer';
import type { RefusedDraft } from './useAgentChat.types';

/** This tab stopped its own turn on `sessionId`, at `at` (`Date.now()`). */
export interface StoppedOwnTurn {
  readonly sessionId: string;
  readonly at: number;
}

/**
 * How long after a Stop a "turn in progress" on that session is taken to be
 * the stopped turn itself. The server keeps the session's lock until the Stop
 * (`stopAgentTurn`) has unwound the run, normally well under a second; it is
 * not released early, since the stopped turn may still be writing. A Stop that
 * never arrived leaves the turn running to its end, which this window outlasts
 * only for short turns.
 */
export const STOPPING_WINDOW_MS = 30_000;

/** Whether a turn reported running on `sessionId` is this tab's own, stopping. */
export function isStoppingOwnTurn(
  stopped: StoppedOwnTurn | null,
  sessionId: string,
  now: number,
): boolean {
  return stopped?.sessionId === sessionId && now - stopped.at < STOPPING_WINDOW_MS;
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** The chat's shared refs and state setters. Stable for the hook's lifetime. */
export interface ChatCore {
  readonly clientMintedRef: MutableRefObject<Set<string>>;
  readonly seededSessionRef: MutableRefObject<string | null>;
  readonly abortRef: MutableRefObject<AbortController | null>;
  readonly currentAssistantIdRef: MutableRefObject<string | null>;
  readonly currentTurnRowIdsRef: MutableRefObject<readonly string[]>;
  readonly unacknowledgedRowIdsRef: MutableRefObject<Set<string>>;
  /**
   * A send was refused and the next history snapshot has not been applied
   * yet. That snapshot re-seeds even when it reports nothing running (the
   * other turn may have finished in between), so the refusal notice is
   * dropped once its answer is there (`useAgentChatHistory`).
   */
  readonly refusalPendingRef: MutableRefObject<boolean>;
  /**
   * The Stop this tab last pressed (`STOPPING_WINDOW_MS`). Cleared by a read
   * that says nothing is running, and with the other refusal state on a
   * session switch, a new conversation and a workspace change.
   */
  readonly stoppedOwnTurnRef: MutableRefObject<StoppedOwnTurn | null>;
  /**
   * The reply this tab stopped last, by row. The run broke off before the
   * server stored it, so no snapshot carries it: it is kept across re-seeds
   * for as long as the session is open, whatever the clock says. Cleared with
   * the other per-session state.
   */
  readonly stoppedReplyRef: MutableRefObject<{
    readonly sessionId: string;
    readonly id: string;
  } | null>;
  readonly setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  readonly setIsStreaming: Dispatch<SetStateAction<boolean>>;
  readonly setIsUploadingTurn: Dispatch<SetStateAction<boolean>>;
  readonly setCurrentAgent: Dispatch<SetStateAction<string | null>>;
  readonly setSessionId: Dispatch<SetStateAction<string>>;
  readonly setRefusedDraft: Dispatch<SetStateAction<RefusedDraft | null>>;
}

export interface ChatCoreState {
  readonly messages: ChatMessage[];
  readonly isStreaming: boolean;
  readonly isUploadingTurn: boolean;
  readonly currentAgent: string | null;
  readonly sessionId: string;
  readonly refusedDraft: RefusedDraft | null;
  readonly chat: ChatCore;
}

export function useAgentChatCore(workspaceId: string | null, userId: string | null): ChatCoreState {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isUploadingTurn, setIsUploadingTurn] = useState(false);
  const [currentAgent, setCurrentAgent] = useState<string | null>(null);
  const [refusedDraft, setRefusedDraft] = useState<RefusedDraft | null>(null);
  // Session ids minted client-side that the server has not been told about
  // yet. Declared BEFORE sessionId so the initial-mount id can register itself:
  // all three paths that can mint an id (mount, workspace switch, New chat)
  // must register, or the un-registered one keeps the certain-404 fetch and its
  // wipe-on-resolve window.
  const clientMintedRef = useRef<Set<string>>(new Set());
  const seededSessionRef = useRef<string | null>(null);
  const [sessionId, setSessionId] = useState(() => {
    const stored = loadStoredSession(userId, workspaceId);
    if (stored) return stored;
    const freshId = generateId();
    clientMintedRef.current.add(freshId);
    seededSessionRef.current = freshId;
    return freshId;
  });
  const abortRef = useRef<AbortController | null>(null);
  // Tracks the assistant-message placeholder for the in-flight turn so
  // cancelMessage can mark it cancelled (preventing late SSE / store
  // updates from appending text after the user hit stop). null when no
  // turn is in flight.
  const currentAssistantIdRef = useRef<string | null>(null);
  /**
   * The unacknowledged ids belonging to the turn currently in flight — the
   * subset `cancelMessage` may release, and nothing else.
   *
   * `cancelMessage` used to `clear()` the whole set, on the premise that "every
   * in-flight row is this turn's". That is false exactly where it matters, and
   * `unacknowledgedRowIdsRef`'s own docblock says why: a send that rejected
   * outright reaches no acknowledging branch, so its rows STAY in the set while
   * a later turn runs. Clearing took them as collateral, and the next seed then
   * deleted the researcher's question along with the row recording that it
   * failed — the deletion the suffix-pointer attempt was reverted for, through
   * a different door.
   *
   * Empty whenever no turn is in flight, which includes a send that rejected:
   * that turn's rows are the server's to never supply, so they must outlive
   * every later cancel.
   */
  const currentTurnRowIdsRef = useRef<readonly string[]>([]);
  /**
   * The rows the agent service has not acknowledged — the ones no history
   * snapshot can contain, and which a seed must therefore keep.
   *
   * A SET, not the suffix pointer this used to be, and the difference is the
   * whole point. The predicate was always "rows the server has not
   * acknowledged"; a single index can only express that while those rows are a
   * contiguous TAIL. A send the service never took, followed by one it did,
   * breaks that — and then no assignment to one pointer is right: keep it and
   * the later turn duplicates, clear it and the earlier one is deleted. Both
   * symptoms are the same missing distinction, which is why three rounds of
   * patching the pointer never landed.
   *
   * Ids go in on every send and come out only on positive evidence the service
   * took the turn: `done`, a cancel (stopping a stream does not unsend it), or
   * an `error` that is NOT flagged `undelivered`. A send that rejected outright
   * reaches none of those, so its rows simply stay.
   */
  const unacknowledgedRowIdsRef = useRef<Set<string>>(new Set());
  const refusalPendingRef = useRef(false);
  const stoppedOwnTurnRef = useRef<StoppedOwnTurn | null>(null);
  const stoppedReplyRef = useRef<{ readonly sessionId: string; readonly id: string } | null>(null);

  // Every member is a ref object or a state setter, both stable for the life
  // of the mount, so this object is too: a callback that lists it as a
  // dependency is exactly as stable as it was when it read them directly.
  // A lazy useState initializer, not useMemo: React guarantees it runs once,
  // where a memo is only a cache hint.
  const [chat] = useState<ChatCore>(() => ({
    clientMintedRef,
    seededSessionRef,
    abortRef,
    currentAssistantIdRef,
    currentTurnRowIdsRef,
    unacknowledgedRowIdsRef,
    refusalPendingRef,
    stoppedOwnTurnRef,
    stoppedReplyRef,
    setMessages,
    setIsStreaming,
    setIsUploadingTurn,
    setCurrentAgent,
    setSessionId,
    setRefusedDraft,
  }));

  return { messages, isStreaming, isUploadingTurn, currentAgent, sessionId, refusedDraft, chat };
}
